// S5 (v0.16.0) — Camera onboarding wizard.
// Four steps: connection (with live test) → details → assignment →
// review. On submit, POST /cameras and poll the detail endpoint for
// stream_validated status.

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  useDeviceProfiles,
  useTestConnection,
  type TestConnectionInput,
  type TestConnectionResponse,
} from "@/api/s5";
import { useProbeDevice } from "@/api/s6";
import { useSites } from "@/api/sites";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { api } from "@/api/client";

type StepId = "connection" | "details" | "assignment" | "review";
type Protocol = "rtsp" | "onvif" | "isapi";

interface WizardForm {
  protocol: Protocol;
  rtsp_url: string;
  ip: string;
  port: number;
  username: string;
  password: string;
  channel: number;
  name: string;
  vendor: string;
  model: string;
  firmware_version: string;
  serial_number: string;
  notes: string;
  site_id: string;
  device_profile_id: string;
  tags: string[];
}

const EMPTY_FORM: WizardForm = {
  protocol: "rtsp",
  rtsp_url: "",
  ip: "",
  port: 80,
  username: "",
  password: "",
  channel: 101,
  name: "",
  vendor: "",
  model: "",
  firmware_version: "",
  serial_number: "",
  notes: "",
  site_id: "",
  device_profile_id: "",
  tags: [],
};

export default function CameraWizardPage() {
  const [step, setStep] = useState<StepId>("connection");
  const [form, setForm] = useState<WizardForm>(EMPTY_FORM);
  const [testResult, setTestResult] = useState<TestConnectionResponse | null>(
    null,
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [tagDraft, setTagDraft] = useState("");
  const test = useTestConnection();
  const probe = useProbeDevice();
  const profiles = useDeviceProfiles();
  const sites = useSites();
  const nav = useNavigate();

  async function handleTest() {
    setTestResult(null);
    const payload: TestConnectionInput = { protocol: form.protocol };
    if (form.protocol === "rtsp") {
      payload.rtsp_url = form.rtsp_url;
      payload.username = form.username || undefined;
      payload.password = form.password || undefined;
    } else {
      payload.ip = form.ip;
      payload.port = form.port;
      payload.username = form.username || undefined;
      payload.password = form.password || undefined;
      if (form.protocol === "isapi") payload.channel = form.channel;
    }
    const r = await test.mutateAsync(payload);
    setTestResult(r);

    // S6 — autofill manufacturer / model / firmware on success. ONVIF
    // gets a richer device info from /discovery/probe; ISAPI's test
    // already returns device_info on the response. RTSP has nothing
    // to autofill.
    if (r.success && form.protocol === "onvif" && form.ip) {
      try {
        const probed = await probe.mutateAsync({
          ip: form.ip,
          port: form.port || 80,
          username: form.username || undefined,
          password: form.password || undefined,
        });
        applyAutofill({
          manufacturer: probed.manufacturer,
          model: probed.model,
          firmware: probed.firmware_version,
        });
      } catch {
        /* probe failed — keep the original test result */
      }
    } else if (r.success && form.protocol === "isapi" && r.device_info) {
      const di = r.device_info as Record<string, unknown>;
      applyAutofill({
        manufacturer: (di.manufacturer as string) ?? null,
        model: (di.model as string) ?? null,
        firmware: (di.firmware as string) ?? null,
      });
    }
  }

  function applyAutofill(info: {
    manufacturer: string | null;
    model: string | null;
    firmware: string | null;
  }) {
    setForm((prev) => ({
      ...prev,
      name:
        prev.name ||
        [info.manufacturer, info.model].filter(Boolean).join(" ") ||
        prev.ip,
      vendor: prev.vendor || info.manufacturer || "",
      model: prev.model || info.model || "",
      firmware_version: prev.firmware_version || info.firmware || "",
    }));
  }

  function addTag() {
    const t = tagDraft.trim();
    if (!t || form.tags.includes(t)) return;
    setForm({ ...form, tags: [...form.tags, t] });
    setTagDraft("");
  }

  async function handleSubmit() {
    setSubmitError(null);
    if (!form.site_id) {
      setSubmitError("Pick a site before submitting");
      return;
    }
    try {
      const payload: Record<string, unknown> = {
        site_id: form.site_id,
        name: form.name,
        protocol: form.protocol,
        rtsp_main_url:
          form.protocol === "rtsp"
            ? form.rtsp_url
            : `rtsp://${form.ip}:${form.port}/Streaming/Channels/${form.channel}`,
        vendor: form.vendor || undefined,
        model: form.model || undefined,
        firmware_version: form.firmware_version || undefined,
        serial_number: form.serial_number || undefined,
        notes: form.notes || undefined,
        tags: form.tags.length ? form.tags : undefined,
        device_profile_id: form.device_profile_id || undefined,
      };
      if (form.protocol !== "rtsp") {
        payload.onvif_host = form.ip;
        payload.onvif_port = form.port;
        payload.onvif_username = form.username || undefined;
        payload.onvif_password = form.password || undefined;
      }
      if (form.username) payload.rtsp_username = form.username;
      if (form.password) payload.rtsp_password = form.password;
      const { data } = await api.post<{ id: string; name: string }>(
        "/api/v1/cameras",
        payload,
      );
      setCreated({ id: data.id, name: data.name });
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        .response?.data?.detail;
      setSubmitError(
        typeof detail === "string" ? detail : "Failed to create camera",
      );
    }
  }

  if (created) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <Card className="border-emerald-500/30">
          <CardContent className="space-y-3 p-4">
            <h2 className="text-lg font-semibold text-emerald-700">
              Camera added: {created.name}
            </h2>
            <p className="text-sm text-muted-foreground">
              Stream validation runs in the background. The camera detail
              page will show the result once it lands.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setForm(EMPTY_FORM);
                  setCreated(null);
                  setStep("connection");
                  setTestResult(null);
                }}
              >
                Add Another
              </Button>
              <Button onClick={() => nav(`/cameras/${created.id}`)}>
                View Camera
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <h1 className="text-2xl font-semibold">Add Camera</h1>
      <Stepper step={step} />

      {step === "connection" && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="grid grid-cols-3 gap-2">
              {(["rtsp", "onvif", "isapi"] as Protocol[]).map((p) => (
                <button
                  key={p}
                  className={
                    "rounded border px-3 py-3 text-sm " +
                    (form.protocol === p
                      ? "border-primary bg-primary/10"
                      : "border-border")
                  }
                  onClick={() => setForm({ ...form, protocol: p })}
                  type="button"
                >
                  {p.toUpperCase()}
                </button>
              ))}
            </div>
            {form.protocol === "rtsp" && (
              <div className="space-y-2">
                <div>
                  <Label>RTSP URL</Label>
                  <Input
                    value={form.rtsp_url}
                    onChange={(e) =>
                      setForm({ ...form, rtsp_url: e.target.value })
                    }
                    placeholder="rtsp://192.168.1.100:554/Streaming/Channels/101"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Username</Label>
                    <Input
                      value={form.username}
                      onChange={(e) =>
                        setForm({ ...form, username: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <Label>Password</Label>
                    <PasswordInput
                      value={form.password}
                      onChange={(e) =>
                        setForm({ ...form, password: e.target.value })
                      }
                    />
                  </div>
                </div>
              </div>
            )}
            {form.protocol !== "rtsp" && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>IP Address</Label>
                  <Input
                    value={form.ip}
                    onChange={(e) => setForm({ ...form, ip: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Port</Label>
                  <Input
                    type="number"
                    value={form.port}
                    onChange={(e) =>
                      setForm({ ...form, port: Number(e.target.value) || 80 })
                    }
                  />
                </div>
                <div>
                  <Label>Username</Label>
                  <Input
                    value={form.username}
                    onChange={(e) =>
                      setForm({ ...form, username: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <PasswordInput
                    value={form.password}
                    onChange={(e) =>
                      setForm({ ...form, password: e.target.value })
                    }
                  />
                </div>
                {form.protocol === "isapi" && (
                  <div>
                    <Label>Channel</Label>
                    <Input
                      type="number"
                      value={form.channel}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          channel: Number(e.target.value) || 101,
                        })
                      }
                    />
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleTest}
                disabled={test.isPending}
              >
                {test.isPending ? "Testing…" : "Test Connection"}
              </Button>
              {testResult && (
                <span
                  className={
                    "text-sm " +
                    (testResult.success ? "text-emerald-600" : "text-red-600")
                  }
                >
                  {testResult.message}
                </span>
              )}
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setStep("details")}>Next</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === "details" && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <div>
              <Label>Camera Name</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Vendor</Label>
                <Input
                  value={form.vendor}
                  onChange={(e) =>
                    setForm({ ...form, vendor: e.target.value })
                  }
                />
              </div>
              <div>
                <Label>Model</Label>
                <Input
                  value={form.model}
                  onChange={(e) => setForm({ ...form, model: e.target.value })}
                />
              </div>
              <div>
                <Label>Firmware Version</Label>
                <Input
                  value={form.firmware_version}
                  onChange={(e) =>
                    setForm({ ...form, firmware_version: e.target.value })
                  }
                />
              </div>
              <div>
                <Label>Serial Number</Label>
                <Input
                  value={form.serial_number}
                  onChange={(e) =>
                    setForm({ ...form, serial_number: e.target.value })
                  }
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <textarea
                rows={3}
                className="w-full rounded border bg-background px-2 py-1 text-sm"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep("connection")}>
                Back
              </Button>
              <Button
                onClick={() => setStep("assignment")}
                disabled={!form.name}
              >
                Next
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === "assignment" && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <div>
              <Label>Site</Label>
              <select
                required
                className="w-full rounded border bg-background px-2 py-1 text-sm"
                value={form.site_id}
                onChange={(e) =>
                  setForm({ ...form, site_id: e.target.value })
                }
              >
                <option value="">Choose a site…</option>
                {(sites.data?.items ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.code ? ` (${s.code})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Device Profile (optional)</Label>
              <select
                className="w-full rounded border bg-background px-2 py-1 text-sm"
                value={form.device_profile_id}
                onChange={(e) =>
                  setForm({ ...form, device_profile_id: e.target.value })
                }
              >
                <option value="">No profile</option>
                {(profiles.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.retention_days}d · {p.recording_mode})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input
                  value={tagDraft}
                  onChange={(e) => setTagDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  placeholder="lobby, atm, ptz…"
                />
                <Button type="button" variant="outline" onClick={addTag}>
                  Add
                </Button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {form.tags.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className="rounded-full bg-muted px-2 py-0.5 text-xs hover:bg-muted/70"
                    onClick={() =>
                      setForm({
                        ...form,
                        tags: form.tags.filter((x) => x !== t),
                      })
                    }
                  >
                    {t} ×
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep("details")}>
                Back
              </Button>
              <Button onClick={() => setStep("review")}>Next</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === "review" && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="text-lg font-semibold">Review</h2>
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <dt className="text-muted-foreground">Protocol</dt>
              <dd>{form.protocol.toUpperCase()}</dd>
              <dt className="text-muted-foreground">Name</dt>
              <dd>{form.name}</dd>
              <dt className="text-muted-foreground">Vendor / Model</dt>
              <dd>
                {form.vendor || "—"} {form.model && ` · ${form.model}`}
              </dd>
              <dt className="text-muted-foreground">Site</dt>
              <dd>{form.site_id || "—"}</dd>
              <dt className="text-muted-foreground">Profile</dt>
              <dd>{form.device_profile_id || "None"}</dd>
              <dt className="text-muted-foreground">Tags</dt>
              <dd>{form.tags.join(", ") || "—"}</dd>
            </dl>
            {submitError && (
              <p className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600">
                {submitError}
              </p>
            )}
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep("assignment")}>
                Back
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" asChild>
                  <Link to="/cameras">Cancel</Link>
                </Button>
                <Button onClick={handleSubmit}>Add Camera</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stepper({ step }: { step: StepId }) {
  const steps: StepId[] = ["connection", "details", "assignment", "review"];
  return (
    <ol className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
      {steps.map((s, i) => {
        const active = s === step;
        return (
          <li
            key={s}
            className={
              active
                ? "rounded bg-primary px-2 py-1 text-primary-foreground"
                : "rounded bg-muted px-2 py-1"
            }
          >
            {i + 1}. {s}
          </li>
        );
      })}
    </ol>
  );
}
